import React from 'react';
import { pipe, not, isNil, filter, mergeDeepRight, groupBy } from 'ramda';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { Row, Column } from 'components/shared/Layout';

import { bookingShape } from 'constants/propTypes';
import { getExtrasOnlyDate } from 'utils/booking';

import { VerticalLine, StyledExtrasContainer, StyledLegContainer } from '../Extras.styles';
import ExtrasJourneyProduct from './ExtrasJourneyProduct';

const defaultStation = {
  uic: '',
  name: '',
  shortCode: ''
};

const defaultLeg = {
  serviceName: '',
  serviceType: { code: '' },
  serviceIdentifier: '',
  timing: { isoDate: '', departs: '' },
  origin: defaultStation,
  destination: defaultStation
};

const defaultExtraShape = {
  origin: defaultStation,
  destination: defaultStation,
  leg: defaultLeg
};

const codeToFilter = ['MARGIN_HOTEL'];

const withDefaults = pipe(filter(pipe(isNil, not)), mergeDeepRight(defaultExtraShape));

const ExtrasOnlyBooking = ({ booking, onCheckboxChange, itemsToCancel, refetch }) => {
  const { outboundExtras, inboundExtras, additionalDetails } = booking;

  const isValidExtra = ({ tariffDetails = {} }) => !codeToFilter.includes(tariffDetails.tariffCode);

  const outbound = outboundExtras.map(withDefaults);
  const inbound = inboundExtras.map(withDefaults);

  const byServiceIdentifier = groupBy((extra) => extra.leg?.serviceIdentifier || 'unknown');

  const outboundExtrasByService = Object.values(byServiceIdentifier(outbound)).filter((extras) =>
    extras.some((extra) => !extra.cancelled && isValidExtra(extra))
  );
  const inboundExtrasByService = Object.values(byServiceIdentifier(inbound)).filter((extras) =>
    extras.some((extra) => !extra.cancelled && isValidExtra(extra))
  );
  const hasExtras = outboundExtrasByService.length || inboundExtrasByService.length;

  const outboundDate = getExtrasOnlyDate(additionalDetails, 'outbound');
  const inboundDate = getExtrasOnlyDate(additionalDetails, 'inbound');

  return (
    <Row span={14} start={2}>
      <Row>
        <h1>
          <FormattedMessage id="extras.title" />
        </h1>
      </Row>

      {hasExtras ? (
        <StyledExtrasContainer>
          <Column span={8}>
            <VerticalLine>
              <h2>
                <FormattedMessage id="extras.outbound" />
              </h2>
              <StyledLegContainer>
                {outboundExtrasByService.map((extras) => {
                  const leg = extras[0].leg;
                  const displayDate = leg?.timing?.isoDate || outboundDate;
                  const hasServiceInfo = leg?.serviceIdentifier;
                  return (
                    <div key={leg?.serviceIdentifier || `${extras[0].origin.name}${extras[0].destination.name}`}>
                      <div data-cy="outbound-info">
                        {displayDate && (
                          <strong>
                            <FormattedDate value={displayDate} year="numeric" month="short" day="numeric" weekday="long" />
                          </strong>
                        )}
                        {hasServiceInfo && leg?.serviceName && (
                          <span data-testid="service-departs">{` - ${leg.serviceType.code}${leg.serviceName} (${leg.timing.departs})`}</span>
                        )}
                        {leg?.origin?.name && displayDate && <span>{hasServiceInfo ? '' : ` - `}</span>}
                        {leg?.origin?.name && (
                          <div data-cy="outbound-extras">
                            {`${leg.origin.name} (${leg.origin.shortCode}) `}
                            <FormattedMessage id="extras.to" />
                            {` ${leg.destination.name} (${leg.destination.shortCode})`}
                          </div>
                        )}
                      </div>
                      <ExtrasJourneyProduct
                        extras={extras}
                        onCheckboxChange={onCheckboxChange}
                        itemsToCancel={itemsToCancel}
                        booking={booking}
                        refetch={refetch}
                      />
                    </div>
                  );
                })}
              </StyledLegContainer>
            </VerticalLine>
          </Column>
          <Column span={8}>
            <h2>
              <FormattedMessage id="extras.inbound" />
            </h2>
            <StyledLegContainer>
              {inboundExtrasByService.map((extras) => {
                const leg = extras[0].leg;
                const displayDate = leg?.timing?.isoDate || inboundDate;
                const hasServiceInfo = leg?.serviceIdentifier;
                return (
                  <div key={leg?.serviceIdentifier || `${extras[0].origin.name}${extras[0].destination.name}`}>
                    <div data-cy="inbound-info">
                      {displayDate && (
                        <strong>
                          <FormattedDate value={displayDate} year="numeric" month="short" day="numeric" weekday="long" />
                        </strong>
                      )}
                      {hasServiceInfo && leg?.serviceName && (
                        <span data-testid="service-departs">{` - ${leg.serviceType.code}${leg.serviceName} (${leg.timing.departs})`}</span>
                      )}
                      {leg?.origin?.name && displayDate && <span>{hasServiceInfo ? '' : ` - `}</span>}
                      {leg?.origin?.name && (
                        <div data-cy="inbound-extras">
                          {`${leg.origin.name} (${leg.origin.shortCode}) `}
                          <FormattedMessage id="extras.to" />
                          {` ${leg.destination.name} (${leg.destination.shortCode})`}
                        </div>
                      )}
                    </div>
                    <ExtrasJourneyProduct extras={extras} onCheckboxChange={onCheckboxChange} itemsToCancel={itemsToCancel} />
                  </div>
                );
              })}
            </StyledLegContainer>
          </Column>
        </StyledExtrasContainer>
      ) : (
        <Column>
          <FormattedMessage id="extras.noProductsBookingLevel" />
        </Column>
      )}
    </Row>
  );
};

ExtrasOnlyBooking.propTypes = {
  booking: bookingShape,
  onCheckboxChange: PropTypes.func,
  itemsToCancel: PropTypes.arrayOf(PropTypes.string),
  refetch: PropTypes.func.isRequired
};

export default ExtrasOnlyBooking;
